// Badge unlock quiz — shown when a learner has reached a badge's points milestone
// but the badge is gated behind a short quiz. Pass it to earn the badge.
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Loader2, X, CheckCircle2, XCircle, Trophy, Share2 } from 'lucide-react';

// Brand-coloured confetti burst for a badge unlock.
function fireConfetti() {
    const colors = ['#FFD400', '#F47920', '#FFFFFF', '#B5202E'];
    const end = Date.now() + 900;
    (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, startVelocity: 42, origin: { y: 0.45 }, colors });
}

export default function BadgeQuizModal({ badgeId, onClose, onEarned }) {
    const [loading, setLoading] = useState(true);
    const [quiz, setQuiz] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get(`/trainee/badges/${badgeId}/quiz`)
            .then((r) => { if (alive) setQuiz(r.data); })
            .catch((err) => {
                if (!alive) return;
                toast.error(err?.response?.data?.detail || 'Could not load the badge quiz');
                onClose?.();
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [badgeId]);

    const allAnswered = quiz && quiz.questions.every((q) => answers[q.id] !== undefined);

    const shareBadge = async () => {
        const name = quiz?.badge?.name || 'a new badge';
        const text = `I just earned the "${name}" badge on Propulse Academy! 🏆`;
        const url = window.location.origin;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'Propulse Academy', text, url });
            } else {
                await navigator.clipboard.writeText(`${text} ${url}`);
                toast.success('Copied to clipboard — paste & share!');
            }
        } catch {
            /* user dismissed the share sheet — no-op */
        }
    };

    const submit = async () => {
        if (!allAnswered) { toast.error('Please answer every question'); return; }
        setSubmitting(true);
        try {
            const r = await api.post(`/trainee/badges/${badgeId}/quiz/attempt`, { answers });
            setResult(r.data);
            if (r.data.passed) {
                toast.success('Badge unlocked! 🏆');
                fireConfetti();
                onEarned?.();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Submission failed');
        } finally { setSubmitting(false); }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose} data-testid="badge-quiz-modal">
                <motion.div
                    className="w-full sm:max-w-lg bg-[#1a1715] text-white rounded-t-3xl sm:rounded-3xl border border-brand-yellow/20 max-h-[90vh] overflow-y-auto"
                    initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="sticky top-0 bg-[#1a1715]/95 backdrop-blur px-5 py-4 flex items-center gap-3 border-b border-white/10 z-10">
                        <div className="h-9 w-9 rounded-xl bg-brand-yellow grid place-items-center text-brand-black">
                            <Award className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-wider text-brand-yellow/70 font-bold">Unlock Badge</div>
                            <div className="font-display font-bold truncate">{quiz?.badge?.name || 'Badge Quiz'}</div>
                        </div>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="badge-quiz-close">
                            <X className="w-5 h-5 text-white/60" />
                        </button>
                    </div>

                    {loading && (
                        <div className="py-16 text-center text-white/50">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                        </div>
                    )}

                    {/* Result screen */}
                    {!loading && result && (
                        <div className="p-8 text-center" data-testid="badge-quiz-result">
                            {result.passed ? (
                                <>
                                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
                                        className="h-20 w-20 rounded-full bg-brand-yellow grid place-items-center mx-auto mb-4">
                                        <Trophy className="w-10 h-10 text-brand-black" />
                                    </motion.div>
                                    <div className="text-2xl font-black text-brand-yellow">Badge Unlocked!</div>
                                    <div className="text-white/70 mt-1">You scored {result.score_percent}% — {quiz?.badge?.name} is yours.</div>
                                    <div className="mt-6 flex items-center justify-center gap-3">
                                        <button onClick={shareBadge} data-testid="badge-quiz-share"
                                            className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold inline-flex items-center gap-2 border border-white/15">
                                            <Share2 className="w-4 h-4" /> Share
                                        </button>
                                        <button onClick={onClose} data-testid="badge-quiz-done"
                                            className="px-6 py-2.5 rounded-xl bg-brand-yellow text-brand-black font-bold">
                                            Done
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="h-20 w-20 rounded-full bg-rose-500/20 grid place-items-center mx-auto mb-4">
                                        <XCircle className="w-10 h-10 text-rose-400" />
                                    </div>
                                    <div className="text-2xl font-black text-white">Almost there</div>
                                    <div className="text-white/70 mt-1">You scored {result.score_percent}%. You need {result.pass_percent}% to unlock. Try again!</div>
                                    <button onClick={onClose} data-testid="badge-quiz-done"
                                        className="mt-6 px-6 py-2.5 rounded-xl bg-brand-yellow text-brand-black font-bold">
                                        Close
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* Questions */}
                    {!loading && quiz && !result && (
                        <div className="p-5 space-y-5">
                            <div className="text-xs text-white/50">Pass mark: <b className="text-brand-yellow">{quiz.pass_percent}%</b> · {quiz.questions.length} question{quiz.questions.length !== 1 ? 's' : ''}</div>
                            {quiz.questions.map((q, qi) => (
                                <div key={q.id} data-testid={`badge-q-${qi}`}>
                                    <div className="font-semibold mb-2 text-sm"><span className="text-brand-yellow">Q{qi + 1}.</span> {q.question}</div>
                                    <div className="space-y-2">
                                        {q.options.map((opt, oi) => {
                                            const active = answers[q.id] === oi;
                                            return (
                                                <button key={oi} type="button"
                                                    onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                                                    data-testid={`badge-q-${qi}-opt-${oi}`}
                                                    className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${active ? 'bg-brand-yellow text-brand-black border-brand-yellow font-bold' : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10'}`}>
                                                    <span className="inline-flex items-center gap-2">
                                                        {active && <CheckCircle2 className="w-4 h-4" />}
                                                        {opt}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            <button onClick={submit} disabled={submitting || !allAnswered}
                                data-testid="badge-quiz-submit"
                                className="w-full py-3 rounded-xl bg-brand-yellow text-brand-black font-bold disabled:opacity-40 inline-flex items-center justify-center gap-2">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                                {submitting ? 'Checking…' : 'Submit & Unlock'}
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
